/**
 * ArchLang v0.10 — the capability registry (§5).
 *
 * Capability-gated getters (`@ver.changes(last 90 days)`, `@tel.verified`) name a
 * capability by CONCEPT, not by backing system (versioning ≠ git). A registry —
 * built on the `widget-namespace.ts` precedent (`libraryPrefix` /
 * `qualifyWidgetTags` off `package.archspace`) — decides which namespaces are
 * known and which functions (`colorize`/`heat`/`css`) are registered:
 *
 *   - **Core curates** the concept names it ships: namespaces `ver` / `tel` and
 *     the render functions `colorize` / `heat` / `css`. Extending this canonical
 *     set is a PR to core (like a keyword).
 *   - **Third parties register** capability namespaces via `package.archspace`
 *     (exactly how `widgets:` works). A registration collision at load — the same
 *     namespace declared by two packages, or a package colliding with a core
 *     name — is an error ({@link CapabilityCollisionError}).
 *
 * "Namespace" means different things per kind, deliberately (design §5): a
 * capability **getter** is dotted (`@acme.metric`) — the top segment is the
 * reserved namespace; a **function** stays bare-headed (`colorize(v)`) in one
 * flat registry, where the load-collision error is the whole protection.
 *
 * Resolution is decoupled from the backing system: the registry knows a namespace
 * EXISTS and what getters it offers; a {@link CapabilityProvider} (git for `ver`,
 * host telemetry for `tel`) is injected per deployment via the eval context. An
 * **absent capability** — an unregistered namespace, an unknown getter, or a
 * registered namespace with no provider wired — is never a hard throw: it routes
 * to a severity-carrying finding (design §4/§5) and evaluation continues.
 */

import type { Diagnostic, DiagnosticSeverity, SourceSpan } from "./types.js";
import type { ExprGetter } from "./selector-ast.js";
import { EXPRESSION_FUNCTIONS } from "./selector-ast.js";

/** A capability getter's scalar result (matches the evaluator's `ExprValue`). */
export type CapabilityValue = number | string | boolean | undefined;

/** How a capability is backed on the local machine — shapes the absent message. */
export type LocalBacking =
  | "git" // versioning — provided by `@archlang/git-io` locally / Studio server
  | "none" // no local backing at all (telemetry — external data only)
  | "host"; // supplied by whatever host wires the provider

/** One getter a capability namespace offers (`@ver.changes`, `@tel.verified`). */
export interface CapabilityGetterSpec {
  readonly name: string;
  readonly summary: string;
  /** Whether the getter accepts `(...)` args (a time window, a filter). */
  readonly takesArgs: boolean;
}

/** A capability namespace: the reserved top getter segment + the getters it offers. */
export interface CapabilityNamespace {
  readonly name: string;
  /** The package that curates it — `"core"` for `ver`/`tel`, else a package name. */
  readonly providedBy: string;
  readonly summary: string;
  readonly getters: readonly CapabilityGetterSpec[];
  readonly localBacking: LocalBacking;
  /** The declaration site (a third-party manifest entry) — anchors a collision. */
  readonly source?: SourceSpan;
}

/**
 * A third-party capability registration collected from a `package.archspace`
 * manifest (mirrors the `widgets:` manifest entry). The package's own name is the
 * `providedBy` identity, exactly as `libraryPrefix` derives a widget prefix from
 * the declaring package.
 */
export interface CapabilityRegistration {
  readonly namespace: string;
  readonly providedBy: string;
  readonly summary?: string;
  readonly getters?: readonly CapabilityGetterSpec[];
  readonly localBacking?: LocalBacking;
  readonly source?: SourceSpan;
}

/** A load-time collision: two packages claim the same capability namespace. */
export class CapabilityCollisionError extends Error {
  constructor(
    readonly namespace: string,
    readonly first: string,
    readonly second: string,
  ) {
    super(
      `capability namespace '${namespace}' is registered by both '${first}' and '${second}' — ` +
        `a namespace may be curated by exactly one package (rename one, per design §5)`,
    );
    this.name = "CapabilityCollisionError";
  }
}

// =============================================================================
// Core-curated namespaces + functions
// =============================================================================

/** `ver` — versioning (git-backed locally). Named by concept: versioning ≠ git. */
export const CORE_VER_NAMESPACE: CapabilityNamespace = {
  name: "ver",
  providedBy: "core",
  summary: "Version-control history of the element's declaring source.",
  localBacking: "git",
  getters: [
    { name: "changes", summary: "Count of commits touching the element's source (optionally within a window).", takesArgs: true },
    { name: "lastChanged", summary: "ISO date of the most recent commit touching the element's source.", takesArgs: false },
    { name: "authors", summary: "Count of distinct authors that touched the element's source (optionally within a window).", takesArgs: true },
  ],
};

/** `tel` — telemetry. Interface defined; NO local backing (external data only). */
export const CORE_TEL_NAMESPACE: CapabilityNamespace = {
  name: "tel",
  providedBy: "core",
  summary: "Runtime telemetry about the element (external observability data).",
  localBacking: "none",
  getters: [
    { name: "verified", summary: "Whether the element has been observed in production traffic.", takesArgs: false },
    { name: "observedCalls", summary: "Observed call volume for the element (optionally within a window).", takesArgs: true },
  ],
};

/** The concept names core ships. Extending this set is a PR to core (like a keyword). */
export const CORE_CAPABILITY_NAMESPACES: readonly CapabilityNamespace[] = [
  CORE_VER_NAMESPACE,
  CORE_TEL_NAMESPACE,
];

/** The render functions core ships (`colorize`/`heat`/`css` — implemented in
 *  `view-style.ts`). Registered here so they read as KNOWN, never "absent". */
export const CORE_CAPABILITY_FUNCTIONS: ReadonlySet<string> = EXPRESSION_FUNCTIONS;

// =============================================================================
// The registry
// =============================================================================

export interface CapabilityRegistry {
  hasNamespace(name: string): boolean;
  namespace(name: string): CapabilityNamespace | undefined;
  /** Whether `name.getter` is a getter the namespace declares. Unknown namespace → false. */
  hasGetter(name: string, getter: string): boolean;
  /** Whether `fn` is a registered function head (`colorize`/`heat`/`css` + registered). */
  hasFunction(fn: string): boolean;
  readonly namespaces: readonly CapabilityNamespace[];
  readonly functions: ReadonlySet<string>;
}

/**
 * Build a capability registry: seed the core-curated namespaces + functions, then
 * fold in third-party registrations (from `package.archspace` manifests). A
 * registration whose namespace is already claimed — by core or by an
 * earlier-registered package — throws {@link CapabilityCollisionError} (the
 * load-time protection, design §5).
 */
export function buildCapabilityRegistry(
  thirdParty: readonly CapabilityRegistration[] = [],
  extraFunctions: readonly { name: string; providedBy: string }[] = [],
): CapabilityRegistry {
  const namespaces = new Map<string, CapabilityNamespace>();
  for (const ns of CORE_CAPABILITY_NAMESPACES) namespaces.set(ns.name, ns);

  for (const reg of thirdParty) {
    const existing = namespaces.get(reg.namespace);
    if (existing) {
      throw new CapabilityCollisionError(reg.namespace, existing.providedBy, reg.providedBy);
    }
    namespaces.set(reg.namespace, {
      name: reg.namespace,
      providedBy: reg.providedBy,
      summary: reg.summary ?? `Capability namespace '${reg.namespace}' provided by ${reg.providedBy}.`,
      localBacking: reg.localBacking ?? "host",
      getters: reg.getters ?? [],
      source: reg.source,
    });
  }

  // Functions live in one flat registry (bare heads, no dotting); a registered
  // head colliding with core or another package is the same load-time error.
  const funcOwners = new Map<string, string>();
  for (const fn of CORE_CAPABILITY_FUNCTIONS) funcOwners.set(fn, "core");
  for (const f of extraFunctions) {
    const prev = funcOwners.get(f.name);
    if (prev) throw new CapabilityCollisionError(f.name, prev, f.providedBy);
    funcOwners.set(f.name, f.providedBy);
  }
  const functions = new Set(funcOwners.keys());

  return {
    hasNamespace: (name) => namespaces.has(name),
    namespace: (name) => namespaces.get(name),
    hasGetter: (name, getter) => {
      const ns = namespaces.get(name);
      // An empty getter list = an OPEN namespace (third party didn't enumerate) —
      // every getter is provisionally accepted; a closed list validates the name.
      return ns ? ns.getters.length === 0 || ns.getters.some((g) => g.name === getter) : false;
    },
    hasFunction: (fn) => functions.has(fn),
    namespaces: [...namespaces.values()],
    functions,
  };
}

/** The core-only registry (no third parties) — used when no env is injected so a
 *  capability getter is still RECOGNIZED (and routed to an absent finding) rather
 *  than silently misread as a field. Built once; immutable. */
let _coreRegistry: CapabilityRegistry | undefined;
export function coreCapabilityRegistry(): CapabilityRegistry {
  return (_coreRegistry ??= buildCapabilityRegistry());
}

// =============================================================================
// Providers + the eval-time environment
// =============================================================================

/** The subject a capability getter resolves against — the bound element, reduced
 *  to what a provider needs: its key + declaring source file (the git path). */
export interface CapabilitySubject {
  readonly key: string;
  /** The element's declaring source file (`source.file`); undefined for spaces. */
  readonly filePath?: string;
}

/**
 * Resolves a namespace's getters against external data. `ver` is backed by
 * `@archlang/git-io` (host-injected — the engine stays git-free); `tel` has no
 * local provider (external data only). A provider returns a scalar, or `undefined`
 * when the getter is KNOWN but has no value for this subject (NOT an absence).
 */
export interface CapabilityProvider {
  resolve(getter: string, rawArgs: string | undefined, subject: CapabilitySubject): CapabilityValue;
}

/** The capability half of the eval context (design §5). Absent = no capability
 *  data wired (render/LSP default): capability getters route to absent findings. */
export interface CapabilityEvalEnv {
  readonly registry: CapabilityRegistry;
  /** A provider per capability namespace (`"ver"` → git-backed). */
  readonly providers?: ReadonlyMap<string, CapabilityProvider>;
  /** Sink for an absent/unavailable capability — the CALLER routes its severity
   *  (a policy at its own severity; a view as a targeted diagnostic, design §4/§5). */
  readonly onAbsent?: (info: AbsentCapability) => void;
}

/** Why a capability getter could not resolve. */
export type AbsentReason =
  | "unregistered-namespace" // `@acme.metric` where `acme` is not registered
  | "unknown-getter" // `@ver.bogus` — `ver` exists but offers no `bogus`
  | "no-provider"; // `@ver.changes` / `@tel.verified` — registered, no provider wired

/** An absent-capability finding — surfaced through {@link CapabilityEvalEnv.onAbsent}. */
export interface AbsentCapability {
  readonly namespace: string;
  readonly getter: string;
  readonly rawArgs?: string;
  readonly reason: AbsentReason;
  readonly message: string;
  /** The getter's span in source — anchors the routed diagnostic. */
  readonly source?: SourceSpan;
  /** How the (registered) namespace is backed — undefined for an unregistered one. */
  readonly localBacking?: LocalBacking;
}

/** The result of routing a `@`-getter through the capability layer. */
export interface CapabilityResolution {
  /** True = this WAS a capability getter (consumed here). False = fall through to
   *  the ordinary field/builtin getter path (`@repo.url` is a plain field). */
  readonly handled: boolean;
  readonly value?: CapabilityValue;
}

const NOT_A_CAPABILITY: CapabilityResolution = { handled: false };

/**
 * Resolve a `@`-sigil getter as a capability, if it is one.
 *
 * Recognition (design §5 — a registered capability RESERVES its top segment): a
 * getter is a capability iff it has `(...)` args (syntactically a capability head)
 * OR it is dotted with a registered top segment. Everything else — `@version`,
 * `@repo.url` — is an ordinary field getter and falls through (`handled: false`).
 *
 * A recognized capability that cannot resolve (unregistered namespace, unknown
 * getter, or a registered namespace with no provider wired) is NOT a throw: it
 * emits an {@link AbsentCapability} through the sink and yields `undefined`, so
 * the enclosing predicate/style stays inert and evaluation continues (design §5).
 */
export function resolveCapabilityGetter(
  g: ExprGetter,
  subject: CapabilitySubject,
  env: CapabilityEvalEnv | undefined,
): CapabilityResolution {
  if (g.sigil !== "@") return NOT_A_CAPABILITY;

  const registry = env?.registry ?? coreCapabilityRegistry();
  const top = g.path[0];
  if (top === undefined) return NOT_A_CAPABILITY;
  const hasArgs = g.capabilityArgs !== undefined;
  const isDotted = g.path.length >= 2;

  const registered = registry.hasNamespace(top);
  // Not a capability at all: a bare/dotted `@` getter with an UNregistered head and
  // no args is a field (`@version`, `@repo.url`). Only args or a registered head
  // make it a capability.
  if (!registered && !hasArgs) return NOT_A_CAPABILITY;

  const getterName = g.path.slice(1).join(".");

  if (!registered) {
    // Had args (`@acme.metric(…)`) but the namespace is unknown → absent.
    return absent(g, env, {
      namespace: top,
      getter: getterName,
      rawArgs: g.capabilityArgs,
      reason: "unregistered-namespace",
      message:
        `capability '${top}' is not registered — no package provides it here. ` +
        `Core curates 'ver'/'tel'; third parties register via package.archspace (design §5).`,
      source: g.source,
    });
  }

  const ns = registry.namespace(top)!;

  if (getterName === "" || !registry.hasGetter(top, getterName)) {
    return absent(g, env, {
      namespace: top,
      getter: getterName,
      rawArgs: g.capabilityArgs,
      reason: "unknown-getter",
      message:
        getterName === ""
          ? `capability '${top}' needs a getter (e.g. @${top}.${ns.getters[0]?.name ?? "…"}).`
          : `capability '${top}' offers no getter '${getterName}'` +
            (ns.getters.length ? ` (has: ${ns.getters.map((x) => x.name).join(", ")}).` : "."),
      source: g.source,
      localBacking: ns.localBacking,
    });
  }

  const provider = env?.providers?.get(top);
  if (!provider) {
    return absent(g, env, {
      namespace: top,
      getter: getterName,
      rawArgs: g.capabilityArgs,
      reason: "no-provider",
      message: noProviderMessage(ns, getterName),
      source: g.source,
      localBacking: ns.localBacking,
    });
  }

  return { handled: true, value: provider.resolve(getterName, g.capabilityArgs, subject) };
}

function noProviderMessage(ns: CapabilityNamespace, getter: string): string {
  const ref = `@${ns.name}.${getter}`;
  switch (ns.localBacking) {
    case "git":
      return `capability '${ref}' requires a versioning provider — provided by git here (wire @archlang/git-io / run under Studio).`;
    case "none":
      return `capability '${ref}' has no local backing — telemetry is external data, not available in this run.`;
    default:
      return `capability '${ref}' has no provider wired in this run.`;
  }
}

function absent(g: ExprGetter, env: CapabilityEvalEnv | undefined, info: AbsentCapability): CapabilityResolution {
  env?.onAbsent?.(info);
  return { handled: true, value: undefined };
}

// =============================================================================
// Severity routing (design §4 — one axis)
// =============================================================================

/** The diagnostic code every routed absent-capability finding surfaces under. */
export const ABSENT_CAPABILITY_CODE = "ABSENT_CAPABILITY";

/**
 * Route an absent capability to a diagnostic at the caller's severity — the design
 * §4 rule: an `error`-severity gate fails closed, a `warning` renders + reports, an
 * `advisory` is a report artifact only (not a diagnostic → `undefined`). Any
 * unknown severity fails closed as `error`. Mirrors `policyDiagnostics`' routing so
 * capability findings share the policy severity axis (the deleted `advisory:` flag).
 */
export function absentCapabilityDiagnostic(info: AbsentCapability, severity: string): Diagnostic | undefined {
  const sev = diagnosticSeverityOf(severity);
  if (!sev) return undefined; // advisory → report artifact, not a diagnostic
  return {
    severity: sev,
    message: info.message,
    source: info.source,
    code: ABSENT_CAPABILITY_CODE,
  };
}

function diagnosticSeverityOf(severity: string): DiagnosticSeverity | undefined {
  if (severity === "warning") return "warning";
  if (severity === "advisory") return undefined;
  return "error"; // `error` + any unknown severity fail closed
}
